// `/urql` subpath, as in `hooks/use-agent-manager-files.ts`: the package root's types
// lag behind the Agent Manager operations.
import { proStudioUrqlGql } from '@pypestream/api-services/urql';

import { fetchAgentManagerFileText } from '../../utils/agent-manager-api';
import { isActionFile } from './action-file-diff';

export interface ActionFilesArgs {
  /** The solution's uuid (`selectedBot.uniqueId`). */
  solutionId: string;
  /** The version's branch name: `v3`, or the draft's. */
  versionName: string;
}

type FileList = {
  files?: readonly ({ path?: string | null } | null)[] | null;
  error?: { message: string };
};

const listPaths = async (list: PromiseLike<FileList>): Promise<string[]> => {
  const { files, error } = await list;
  if (error) throw new Error(error.message);
  return (files ?? [])
    .map((file) => file?.path)
    .filter((path): path is string => !!path);
};

/**
 * A version's action code — its `services/` scripts and top-level `.py` files —
 * keyed by path, read from Agent Manager like the Actions page reads it
 * (PSTUDIO-4789). It used to come from Bot Manager's `getVersionById` /
 * `getVersionFile`, a rail retired with PSTUDIO-3336 that fails for every
 * solution.
 */
const readFresh = async (
  args: ActionFilesArgs
): Promise<Record<string, string>> => {
  const [scripts, configs] = await Promise.all([
    listPaths(
      proStudioUrqlGql
        .getScriptsFiles(args)
        .then((r) => ({ files: r.data?.admin_?.scriptsFiles, error: r.error }))
    ),
    listPaths(
      proStudioUrqlGql
        .getConfigsFiles(args)
        .then((r) => ({ files: r.data?.admin_?.configsFiles, error: r.error }))
    ),
  ]);
  const entries = await Promise.all(
    [...scripts, ...configs]
      .filter(isActionFile)
      .map(
        async (path) =>
          [path, await fetchAgentManagerFileText({ ...args, path })] as const
      )
  );
  return Object.fromEntries(entries);
};

const inFlight = new Map<string, Promise<Record<string, string>>>();

const keyOf = ({ solutionId, versionName }: ActionFilesArgs) =>
  `${solutionId}:${versionName}`;

/**
 * Reads a version's action files, sharing a read still in progress: a panel
 * that remounts (StrictMode, a quick reopen) doesn't fetch every file twice.
 * A settled read isn't kept — the draft changes, so it is read again.
 */
export const readActionFiles = (
  args: ActionFilesArgs
): Promise<Record<string, string>> => {
  const key = keyOf(args);
  let read = inFlight.get(key);
  if (!read) {
    read = readFresh(args);
    inFlight.set(key, read);
    const forget = () => inFlight.delete(key);
    read.then(forget, forget);
  }
  return read;
};

/** Published versions don't change: each is read once a session. */
const publishedCache = new Map<string, Promise<Record<string, string>>>();

/**
 * {@link readActionFiles} for a published version, cached for the session. A
 * failed read is dropped, so the next time Deploy opens it asks again — once,
 * not on every render: overlapping reads share one request.
 */
export const readPublishedActionFiles = (
  args: ActionFilesArgs
): Promise<Record<string, string>> => {
  const key = keyOf(args);
  let read = publishedCache.get(key);
  if (!read) {
    read = readActionFiles(args);
    publishedCache.set(key, read);
    read.catch(() => publishedCache.delete(key));
  }
  return read;
};
